import React, { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Download,
  FileSpreadsheet,
  Calendar,
  Printer,
  Filter,
  XCircle,
  Edit3,
  AlertTriangle,
  BarChart3,
} from "lucide-react";
import { cn } from "../lib/utils";
import {
  server_post_data,
  get_kot_modification_report,
} from "../ServiceConnection/serviceconnection";
import { retrieveData } from "../LocalConnection/LocalConnection.js";
import * as XLSX from "xlsx";
import Loader from "../components/layout/Loader";

// ─── Maps ─────────────────────────────────────────────────────────────────────
const ORDER_STATUS_MAP = {
  0: "Pending",
  1: "Active",
  2: "Completed",
  3: "Cancelled",
};
const ORDER_TYPE_MAP = {
  0: "Dine In",
  1: "Takeaway",
  2: "Delivery",
  3: "Dine In",
  4: "Drive Through",
};

const fmtDate = (val) => {
  if (!val) return "-";
  try {
    return new Date(val).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(val);
  }
};

const fmtPrice = (val) =>
  val != null ? `₹${parseFloat(val).toFixed(2)}` : "-";

// ─── Component ────────────────────────────────────────────────────────────────
export default function OrderModificationReport() {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [reportStats, setReportStats] = useState({});
  const [dateFilter, setDateFilter] = useState("today");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchItem, setSearchItem] = useState("");

  const restaurantName = retrieveData("restaurant_name") || "UpRestro";
  const restaurantAddress = retrieveData("restaurant_address") || "";

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchReportData = useCallback(async () => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    const fd = new FormData();
    fd.append("start_date", fromDate);
    fd.append("end_date", toDate);
    try {
      const res = await server_post_data(get_kot_modification_report, fd);
      if (!res.data.error) {
        const msg = res.data.message;
        let records = msg.results || [];
        if (searchItem.trim())
          records = records.filter((r) =>
            r.item_name?.toLowerCase().includes(searchItem.toLowerCase()),
          );
        setReportData(records);
        setReportStats({
          total_cancelled_items: msg.total_cancelled_items || 0,
          total_modified_items: msg.total_modified_items || 0,
          date_range: msg.date_range || "",
        });
      } else {
        setReportData([]);
        setReportStats({});
      }
    } catch (e) {
      console.error(e);
      setReportData([]);
      setReportStats({});
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, searchItem]);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setFromDate(today);
    setToDate(today);
  }, []);

  useEffect(() => {
    if (fromDate && toDate) fetchReportData();
  }, [fromDate, toDate]);

  // ── Quick date ─────────────────────────────────────────────────────────────
  const handleQuickDate = (f) => {
    setDateFilter(f);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fmt = (d) => d.toISOString().split("T")[0];
    if (f === "today") {
      setFromDate(fmt(today));
      setToDate(fmt(today));
    }
    if (f === "yesterday") {
      const d = new Date(today);
      d.setDate(d.getDate() - 1);
      setFromDate(fmt(d));
      setToDate(fmt(d));
    }
    if (f === "week") {
      const d = new Date(today);
      d.setDate(d.getDate() - 7);
      setFromDate(fmt(d));
      setToDate(fmt(today));
    }
    if (f === "month") {
      const d = new Date(today);
      d.setMonth(d.getMonth() - 1);
      setFromDate(fmt(d));
      setToDate(fmt(today));
    }
  };

  // ── Excel Export ───────────────────────────────────────────────────────────
  const exportToExcel = () => {
    const COLS = 16;
    const wb = XLSX.utils.book_new();
    const ws = {
      "!merges": [],
      "!rows": [],
      "!cols": [
        { wch: 10 }, // KOT No.
        { wch: 13 }, // Order Type
        { wch: 20 }, // Customer Name
        { wch: 14 }, // Customer Phone
        { wch: 12 }, // Status
        { wch: 30 }, // Reason
        { wch: 20 }, // Created
        { wch: 20 }, // Bill Print Date
        { wch: 14 }, // Complete Duration
        { wch: 30 }, // Item Name
        { wch: 20 }, // Item Modified Date
        { wch: 18 }, // User
        { wch: 10 }, // Old Value
        { wch: 10 }, // New Value
        { wch: 14 }, // KOT Item Status
        { wch: 12 }, // Price
      ],
    };

    let r = 0;

    const sc = (row, col, value, style = {}) => {
      const a = XLSX.utils.encode_cell({ r: row, c: col });
      ws[a] = {
        t: typeof value === "number" ? "n" : "s",
        v: value ?? "",
        s: style,
      };
    };
    const mr = (row, from, to) =>
      ws["!merges"].push({ s: { r: row, c: from }, e: { r: row, c: to } });

    // Title
    sc(r, 0, "KOT MODIFICATION & CANCELLATION REPORT", {
      font: { bold: true, sz: 16, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "1E3A5F" } },
      alignment: { horizontal: "center", vertical: "center" },
    });
    mr(r, 0, COLS - 1);
    ws["!rows"][r] = { hpt: 28 };
    r++;

    sc(r, 0, restaurantName, {
      font: { bold: true, sz: 13 },
      alignment: { horizontal: "center" },
    });
    mr(r, 0, COLS - 1);
    r++;

    if (restaurantAddress) {
      sc(r, 0, restaurantAddress, {
        font: { sz: 10, italic: true },
        alignment: { horizontal: "center" },
      });
      mr(r, 0, COLS - 1);
      r++;
    }
    r++;

    // Period row
    const metaSt = { font: { sz: 10 }, fill: { fgColor: { rgb: "F0F4F8" } } };
    sc(
      r,
      0,
      `Period: ${reportStats.date_range || `${fromDate} to ${toDate}`}`,
      metaSt,
    );
    mr(r, 0, 7);
    sc(
      r,
      8,
      `Generated: ${new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}`,
      metaSt,
    );
    mr(r, 8, COLS - 1);
    r++;
    r++;

    // Summary boxes
    const sumSt = (bg, txt) => ({
      font: { bold: true, sz: 11, color: { rgb: txt } },
      fill: { fgColor: { rgb: bg } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
      },
    });
    const summaries = [
      [
        "Total Cancelled",
        reportStats.total_cancelled_items || 0,
        "FFE6E6",
        "CC0000",
      ],
      [
        "Total Modified",
        reportStats.total_modified_items || 0,
        "FFF4E6",
        "996300",
      ],
      ["Total Records", reportData.length, "E6F0FF", "003399"],
    ];
    summaries.forEach(([lbl, val, bg, txt], i) => {
      const c = i * 5;
      sc(r, c, lbl, sumSt("D0D8E4", "333333"));
      mr(r, c, c + 3);
      sc(r, c + 1, val, sumSt(bg, txt));
    });
    ws["!rows"][r] = { hpt: 22 };
    r++;
    r++;

    // Headers
    const headers = [
      "KOT No.",
      "Order Type",
      "Customer Name",
      "Customer Phone",
      "Status",
      "Reason",
      "Created",
      "Bill Print Date",
      "Complete Duration",
      "Item Name",
      "Item Modified Date",
      "User",
      "Old Value",
      "New Value",
      "KOT Item Status",
      "Price",
    ];
    const hdrSt = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
      fill: { fgColor: { rgb: "2C5F8A" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };
    headers.forEach((h, c) => sc(r, c, h, hdrSt));
    ws["!rows"][r] = { hpt: 20 };
    r++;

    // Data rows
    reportData.forEach((rec, idx) => {
      const cancelled = rec.kot_item_status === "Cancelled";
      const bg = cancelled
        ? idx % 2 === 0
          ? "FFE6E6"
          : "FFD6D6"
        : idx % 2 === 0
          ? "FFF8E6"
          : "FFF0D0";

      const cs = (bold = false, color = "000000", center = false) => ({
        font: { sz: 10, bold, color: { rgb: color } },
        fill: { fgColor: { rgb: bg } },
        alignment: {
          horizontal: center ? "center" : "left",
          vertical: "center",
          wrapText: true,
        },
        border: {
          top: { style: "thin", color: { rgb: "D0D0D0" } },
          bottom: { style: "thin", color: { rgb: "D0D0D0" } },
          left: { style: "thin", color: { rgb: "D0D0D0" } },
          right: { style: "thin", color: { rgb: "D0D0D0" } },
        },
      });

      const row = [
        rec.kot_no || "-",
        (ORDER_TYPE_MAP[rec.order_type] || rec.order_type || "-") + (rec.table_code_names ? ` (${rec.table_code_names})` : ""),
        rec.guest_name || "-",
        rec.guest_mobile_no || "-",
        ORDER_STATUS_MAP[rec.order_status] || rec.order_status || "-",
        rec.reason || "-",
        fmtDate(rec.created),
        fmtDate(rec.update_date),
        rec.complete_duration || "-",
        rec.item_name || "-",
        fmtDate(rec.item_modified_date),
        rec.admin_name || "-",
        rec.old_value ?? 0,
        rec.new_value ?? 0,
        rec.kot_item_status || "-",
        rec.price != null ? parseFloat(rec.price) : 0,
      ];

      row.forEach((val, c) => {
        let style;
        if (c === 0) style = cs(true, "1E3A8A", true); // KOT No bold
        if (c === 9) style = cs(false, "000000", false); // Item Name
        if (c === 12 || c === 13) style = cs(true, "000000", true); // Old/New
        if (c === 14) style = cs(true, cancelled ? "CC0000" : "996300", true);
        if (c === 15) style = cs(true, "1A6B2A", true); // Price green
        if (!style) style = cs();
        sc(r, c, val, style);
      });
      r++;
    });

    // Footer
    r++;
    sc(
      r,
      0,
      `Cancelled: ${reportStats.total_cancelled_items || 0}  |  Modified: ${reportStats.total_modified_items || 0}  |  Total: ${reportData.length}`,
      {
        font: { bold: true, sz: 10, italic: true },
        fill: { fgColor: { rgb: "E8EEF4" } },
      },
    );
    mr(r, 0, COLS - 1);

    ws["!ref"] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r, c: COLS - 1 },
    });
    XLSX.utils.book_append_sheet(wb, ws, "KOT Modifications");
    XLSX.writeFile(wb, `KOT_Modification_Report_${fromDate}_to_${toDate}.xlsx`);
  };

  const cancelledCount = reportData.filter(
    (r) => r.kot_item_status === "Cancelled",
  ).length;
  const modifiedCount = reportData.filter(
    (r) => r.kot_item_status === "Active",
  ).length;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <MainLayout>
      <Header
        title="KOT Modification & Cancellation Report"
        subtitle="Track KOT-level modifications and cancellations"
      />
      {loading && <Loader />}

      <div className="p-6 space-y-6">
        {/* ── Filters ── */}
        <Card className="border-primary/20 print:hidden">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="w-5 h-5" /> Report Filters
                </CardTitle>
                <CardDescription>
                  Configure your report parameters
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => window.print()}
                  variant="outline"
                  className="gap-2"
                >
                  <Printer className="w-4 h-4" /> Print
                </Button>
                <Button onClick={exportToExcel} className="gap-2">
                  <Download className="w-4 h-4" /> Export Excel
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Search by Item</Label>
                <Input
                  placeholder="e.g., Butter Naan"
                  value={searchItem}
                  onChange={(e) => setSearchItem(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Report Period
              </Label>
              <div className="flex flex-wrap gap-2">
                {[
                  ["today", "Today"],
                  ["yesterday", "Yesterday"],
                  ["week", "Last 7 Days"],
                  ["month", "Last 30 Days"],
                  ["custom", "Custom Range"],
                ].map(([f, label]) => (
                  <Button
                    key={f}
                    variant={dateFilter === f ? "default" : "outline"}
                    size="sm"
                    onClick={() =>
                      f === "custom"
                        ? setDateFilter("custom")
                        : handleQuickDate(f)
                    }
                  >
                    {label}
                  </Button>
                ))}
              </div>

              {dateFilter === "custom" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg border">
                  <div className="space-y-2">
                    <Label>From Date</Label>
                    <Input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>To Date</Label>
                    <Input
                      type="date"
                      value={toDate}
                      min={fromDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            <Button
              onClick={fetchReportData}
              className="w-full gap-2"
              size="lg"
            >
              <BarChart3 className="w-4 h-4" /> Generate Report
            </Button>
          </CardContent>
        </Card>

        {/* ── Report Body ── */}
        <div className="space-y-6 print:space-y-4">
          {/* Summary Card */}
          <Card className="border-2 print:border-0 print:shadow-none">
            <CardContent className="pt-6">
              <div className="text-center space-y-1 border-b pb-4 mb-4">
                <h1 className="text-2xl font-bold text-primary print:text-black">
                  {restaurantName}
                </h1>
                {restaurantAddress && (
                  <p className="text-sm text-muted-foreground">
                    {restaurantAddress}
                  </p>
                )}
                <h2 className="text-lg font-semibold mt-2">
                  KOT MODIFICATION & CANCELLATION REPORT
                </h2>
                <p className="text-sm text-muted-foreground">
                  Period:{" "}
                  <strong>
                    {reportStats.date_range || `${fromDate} to ${toDate}`}
                  </strong>
                </p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-red-50 p-4 rounded-lg text-center">
                  <p className="text-xs text-red-700">Items Cancelled</p>
                  <p className="text-2xl font-bold text-red-600">
                    {reportStats.total_cancelled_items || 0}
                  </p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg text-center">
                  <p className="text-xs text-amber-700">Items Modified</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {reportStats.total_modified_items || 0}
                  </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg text-center">
                  <p className="text-xs text-blue-700">Total Records</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {reportData.length}
                  </p>
                </div>
                <div className="bg-primary/5 p-4 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground">
                    Active (Modified)
                  </p>
                  <p className="text-2xl font-bold text-primary">
                    {modifiedCount}
                  </p>
                </div>
              </div>

              {reportStats.total_cancelled_items > 10 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
                  <p className="text-sm text-red-700">
                    High Cancellation Alert: {reportStats.total_cancelled_items}{" "}
                    items cancelled in selected period.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Detail Table */}
          <Card className="print:border-0 print:shadow-none">
            <CardHeader className="print:pb-2">
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" /> Detailed KOT Records
              </CardTitle>
              <CardDescription>
                Showing {reportData.length} records
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border overflow-auto print:border-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 print:bg-gray-100">
                      <TableHead className="w-10">#</TableHead>
                      <TableHead>KOT No.</TableHead>
                      <TableHead>Order Type</TableHead>
                      <TableHead>Customer Name</TableHead>
                      <TableHead>Customer Phone</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Bill Print Date</TableHead>
                      <TableHead>Complete Duration</TableHead>
                      <TableHead>Item Name</TableHead>
                      <TableHead>Item Modified Date</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead className="text-center">Old Value</TableHead>
                      <TableHead className="text-center">New Value</TableHead>
                      <TableHead className="text-center">
                        KOT Item Status
                      </TableHead>
                      <TableHead className="text-right">Price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={17}
                          className="text-center py-12 text-muted-foreground"
                        >
                          No records found for selected period
                        </TableCell>
                      </TableRow>
                    ) : (
                      reportData.map((rec, idx) => {
                        const cancelled = rec.kot_item_status === "Cancelled";
                        return (
                          <TableRow
                            key={idx}
                            className={
                              cancelled ? "bg-red-50/50" : "bg-amber-50/30"
                            }
                          >
                            <TableCell className="font-medium">
                              {idx + 1}
                            </TableCell>
                            <TableCell className="font-bold text-primary whitespace-nowrap">
                              {rec.kot_no || "-"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {ORDER_TYPE_MAP[rec.order_type] ||
                                rec.order_type ||
                                "-"}
                              {rec.table_code_names
                                ? ` (${rec.table_code_names})`
                                : ""}
                            </TableCell>
                            <TableCell>{rec.guest_name || "-"}</TableCell>
                            <TableCell>{rec.guest_mobile_no || "-"}</TableCell>
                            <TableCell>
                              {ORDER_STATUS_MAP[rec.order_status] ||
                                rec.order_status ||
                                "-"}
                            </TableCell>
                            <TableCell
                              className="max-w-[160px] truncate"
                              title={rec.reason}
                            >
                              {rec.reason || "-"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {fmtDate(rec.created)}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {fmtDate(rec.update_date)}
                            </TableCell>
                            <TableCell className="whitespace-nowrap font-medium">
                              {rec.complete_duration || "-"}
                            </TableCell>
                            <TableCell className="font-medium">
                              {rec.item_name || "-"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {fmtDate(rec.item_modified_date)}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {rec.admin_name || "-"}
                            </TableCell>
                            <TableCell className="text-center font-medium">
                              {rec.old_value ?? 0}
                            </TableCell>
                            <TableCell className="text-center font-medium">
                              {rec.new_value ?? 0}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge
                                className={cn(
                                  "font-medium border gap-1",
                                  cancelled
                                    ? "bg-red-100 text-red-700 border-red-300"
                                    : "bg-amber-100 text-amber-700 border-amber-300",
                                )}
                              >
                                {cancelled ? (
                                  <XCircle className="w-3 h-3" />
                                ) : (
                                  <Edit3 className="w-3 h-3" />
                                )}
                                {rec.kot_item_status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right font-medium text-green-700">
                              {fmtPrice(rec.price)}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="mt-4 pt-4 border-t flex justify-between text-sm text-muted-foreground">
                <span>
                  Total Records: <strong>{reportData.length}</strong>
                </span>
                <span>
                  Cancelled:{" "}
                  <strong className="text-red-600">{cancelledCount}</strong>
                  &nbsp;|&nbsp; Modified:{" "}
                  <strong className="text-amber-600">{modifiedCount}</strong>
                </span>
                <span>
                  Generated:{" "}
                  {new Date().toLocaleString("en-IN", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
}
